import React, { CSSProperties, useCallback, useEffect, useMemo, useRef } from 'react';

import { Layout } from 'client/app/apps/disco-prototype/components/PlateMapCanvas/common';
import {
  clearAndScale,
  drawBorder,
  drawHeaders,
  drawWells,
} from 'client/app/apps/disco-prototype/components/PlateMapCanvas/draw';
import { wellIdAtPoint } from 'client/app/apps/disco-prototype/components/PlateMapCanvas/wellHitTest';

type Well = {
  /** Location identifier (e.g. "A1", "B12" ) */
  id: string;
  /** Fill colour in any valid Canvas‑2D colour format */
  color: string;
};

type PlateMapCanvasProps = {
  wells: Well[];
  numRows?: number; // default 8
  numColumns?: number; // default 12
  showHeaders?: boolean; // default true
  className?: string; // utility CSS classes for wrapper
  style?: CSSProperties; // inline‑style override
  onWellEnter?: (id: string | null, e: React.MouseEvent<HTMLCanvasElement>) => void;
  onWellLeave?: () => void;
};

const DEFAULT_ROWS = 8;
const DEFAULT_COLUMNS = 12;
const TRAILING_PAD = 0.25;

const PlateMapCanvas: React.FC<PlateMapCanvasProps> = ({
  wells,
  numRows = DEFAULT_ROWS,
  numColumns = DEFAULT_COLUMNS,
  showHeaders = true,
  className = '',
  style,
  onWellEnter,
  onWellLeave,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const layoutRef = useRef<Layout | null>(null);
  const lastHoverRef = useRef<string | null>(null);

  // Colour lookup → O(1)
  const colourMap = useMemo(() => {
    const map: Record<string, string> = {};
    wells.forEach(({ id, color }) => {
      map[id] = color;
    });
    return map;
  }, [wells]);

  /* ---------------------------------------------------------------------
   *  Master draw routine — orchestrates individual drawing steps.
   * -------------------------------------------------------------------*/
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // ───── backing‑store sizing (HiDPI safe) ─────
    const dpr = window.devicePixelRatio || 1;
    const { width: cssW, height: cssH } = container.getBoundingClientRect();
    const width = Math.floor(cssW * dpr);
    const height = Math.floor(cssH * dpr);
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    ctx.reset();
    ctx.scale(dpr, dpr);

    // ───── layout calc ─────
    const headerOffset = showHeaders ? 1 : TRAILING_PAD;
    const totalCols = numColumns + headerOffset + TRAILING_PAD;
    const totalRows = numRows + headerOffset + TRAILING_PAD;

    const cellW = cssW / totalCols;
    const cellH = cssH / totalRows;
    const radius = Math.min(cellW, cellH) * 0.4;

    const layout: Layout = {
      cellW,
      cellH,
      radius,
      headerOffset,
      numColumns,
      numRows,
      showHeaders,
    };

    layoutRef.current = layout;

    // ───── ordered drawing pipeline ─────
    clearAndScale(ctx, cssW, cssH);
    drawBorder(ctx, cssW, cssH);
    drawHeaders(ctx, layout);
    drawWells(ctx, layout, colourMap);
  }, [numColumns, numRows, showHeaders, colourMap]);

  /* ──────────────────────────
   *  Pointer handling
   * ─────────────────────────*/
  const handleMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const { offsetX, offsetY } = e.nativeEvent;

      const id = wellIdAtPoint(layoutRef.current, offsetX, offsetY);

      if (id === null) {
        if (lastHoverRef.current !== null) {
          onWellLeave?.();
        }
        lastHoverRef.current = null;
        return;
      }

      if (id !== lastHoverRef.current) {
        lastHoverRef.current = id;
        onWellEnter?.(id, e);
      }
    },
    [onWellEnter, onWellLeave],
  );

  /* ---------------------------------------------------------------------
   *  Lifecycle & resize handling
   * -------------------------------------------------------------------*/
  const handleResize = useCallback(() => requestAnimationFrame(draw), [draw]);

  useEffect(draw, [draw]);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(handleResize);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [handleResize]);

  /* ---------------------------------------------------------------------
   *  Render
   * -------------------------------------------------------------------*/
  const aspectRatio = showHeaders
    ? `${numColumns + 1 + TRAILING_PAD} / ${numRows + 1 + TRAILING_PAD}`
    : `${numColumns + TRAILING_PAD} / ${numRows + TRAILING_PAD}`;

  const mergedStyle: CSSProperties = {
    aspectRatio,
    position: 'relative',
    width: '100%',
    ...style,
  };

  return (
    <div ref={containerRef} className={className} style={mergedStyle}>
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
        }}
        onMouseMove={handleMove}
      />
    </div>
  );
};

export default PlateMapCanvas;
